import { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { ApiError, getToken, tenants as tenantsApi } from "../lib/api";
import type { ChurnPoint, PlatformMetrics, TenantBrief } from "../lib/api";
import { useEscapeKey } from "../lib/useEscapeKey";

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://api.mipuebloapp.com";

function pct(n: number | null): string {
  return n === null ? "—" : `${n}%`;
}

type Range = 7 | 30 | 90 | 180 | 365;
const RANGES: { value: Range; label: string }[] = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
  { value: 180, label: "180 días" },
  { value: 365, label: "12 meses" },
];

const FEATURE_KEY: Record<string, string> = {
  WhatsApp: "whatsapp",
  Email: "email",
  Telegram: "telegram",
  Messenger: "messenger",
  "Firma digital": "firma_digital",
  "Centro de ayuda": "kb",
};

export function PlatformMetricsPage() {
  const [data, setData] = useState<PlatformMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<Range>(30);
  const [drilldown, setDrilldown] = useState<{
    title: string;
    items: TenantBrief[];
    loading: boolean;
  } | null>(null);

  const load = (d: Range) => {
    setLoading(true);
    setError(null);
    tenantsApi
      .platformMetrics(d)
      .then(setData)
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Error de red"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const openFeatureDrilldown = async (feature: string) => {
    const key = FEATURE_KEY[feature] ?? feature.toLowerCase();
    setDrilldown({ title: `Feature: ${feature}`, items: [], loading: true });
    try {
      const items = await tenantsApi.platformMetricsTenantsByFeature(key);
      setDrilldown({ title: `Feature: ${feature}`, items, loading: false });
    } catch {
      setDrilldown(null);
      setError("No se pudo cargar el detalle de la feature");
    }
  };

  const downloadReport = async (format: "pdf" | "xlsx") => {
    const token = getToken();
    const res = await fetch(
      `${API_BASE}/admin/platform/metrics/report.${format}?days=${days}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      setError(`No se pudo generar el informe (${res.status})`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      res.headers.get("content-disposition")?.match(/filename="?([^"]+)"?/)?.[1] ??
      `mipueblo-informe.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Shell>
      <section className="page">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          <div>
            <div className="eyebrow">Plataforma · Métricas</div>
            <h1>Métricas e informes</h1>
            <p className="lede" style={{ marginBottom: 0 }}>
              Salud comercial y de uso de MiPueblo cross-tenant.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div role="group" aria-label="Rango temporal" style={{ display: "flex", gap: 4 }}>
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setDays(r.value)}
                  aria-pressed={days === r.value}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--hairline-strong)",
                    background: days === r.value ? "var(--ink)" : "transparent",
                    color: days === r.value ? "#fff" : "var(--ink-2)",
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div style={{ width: 1, height: 28, background: "var(--hairline-strong)", margin: "0 8px" }} />
            <button
              type="button"
              className="btn-outline"
              onClick={() => void downloadReport("pdf")}
              title="Descargar informe PDF"
            >
              PDF
            </button>
            <button
              type="button"
              className="btn-outline"
              onClick={() => void downloadReport("xlsx")}
              title="Descargar informe Excel"
            >
              Excel
            </button>
          </div>
        </div>

        {error && <div className="alert err">{error}</div>}

        {loading || !data ? (
          <div className="eyebrow">Cargando…</div>
        ) : (
          <>
            <h2 style={sectionTitleStyle}>Tenants</h2>
            <div style={kpiGridStyle}>
              <KPI
                label="Tenants facturables"
                value={String(data.tenants_facturables)}
                hint={`${data.tenants_active} activos · ${data.tenants_trial} trial`}
              />
              <KPI
                label="Suspendidos"
                value={String(data.tenants_suspended)}
                hint={`${data.tenants_cancelled} cancelados total`}
              />
              <KPI
                label="Conversión trial→activo"
                value={pct(data.trial_to_active_pct)}
                hint="De los activos con fecha de activación"
              />
              <KPI
                label={`Churn ${days}d`}
                value={pct(data.churn_30d_pct)}
                hint={`${data.tenants_cancelled} cancelados total`}
                tone={
                  data.churn_30d_pct !== null && data.churn_30d_pct > 5
                    ? "danger"
                    : undefined
                }
              />
            </div>

            <h2 style={sectionTitleStyle}>Uso de la plataforma</h2>
            <div style={kpiGridStyle}>
              <KPI
                label="PQRSDF total"
                value={data.pqrsdf_total.toLocaleString("es-CO")}
                hint={`${data.pqrsdf_mes_actual} en la ventana`}
              />
              <KPI
                label="Funcionarios activos"
                value={data.funcionarios_activos.toLocaleString("es-CO")}
                hint="Cross-tenant"
              />
              <KPI
                label="Encuestas respondidas"
                value={data.encuestas_respondidas.toLocaleString("es-CO")}
                hint={
                  data.rating_promedio_plataforma !== null
                    ? `Rating promedio ${data.rating_promedio_plataforma}/5`
                    : "Sin datos"
                }
              />
              <KPI
                label="Artículos KB publicados"
                value={data.kb_articulos_publicados.toLocaleString("es-CO")}
                hint="Cross-tenant"
              />
            </div>

            <h2 style={sectionTitleStyle}>Adopción por feature</h2>
            <div className="card">
              <div className="card-inner" style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--hairline-strong)" }}>
                      <Th>Feature</Th>
                      <Th>Con</Th>
                      <Th>Total</Th>
                      <Th>%</Th>
                      <Th>{null}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.adopcion.map((a) => (
                      <tr
                        key={a.feature}
                        style={{
                          borderBottom: "1px solid var(--hairline)",
                          cursor: "pointer",
                        }}
                        onClick={() => void openFeatureDrilldown(a.feature)}
                      >
                        <Td>
                          <div style={{ fontWeight: 600 }}>{a.feature}</div>
                        </Td>
                        <Td>{a.tenants_con}</Td>
                        <Td>{a.tenants_total}</Td>
                        <Td mono>{a.pct}%</Td>
                        <Td>
                          <div
                            style={{
                              background: "var(--hairline)",
                              height: 6,
                              borderRadius: 3,
                              overflow: "hidden",
                              width: 180,
                            }}
                          >
                            <div
                              style={{
                                background: "var(--ok)",
                                height: "100%",
                                width: `${Math.min(100, a.pct)}%`,
                              }}
                            />
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <h2 style={sectionTitleStyle}>Churn — últimos 12 meses</h2>
            <ChurnChart serie={data.churn_serie_12m} />
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-inner" style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--hairline-strong)" }}>
                      <Th>Mes</Th>
                      <Th>Nuevos</Th>
                      <Th>Cancelados</Th>
                      <Th>Netos</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.churn_serie_12m.map((c) => (
                      <tr
                        key={c.mes}
                        style={{ borderBottom: "1px solid var(--hairline)" }}
                      >
                        <Td mono>{c.mes}</Td>
                        <Td>
                          <span style={{ color: "var(--ok)" }}>+{c.nuevos}</span>
                        </Td>
                        <Td>
                          <span
                            style={{
                              color:
                                c.cancelados > 0 ? "var(--err)" : "var(--ink-3)",
                            }}
                          >
                            {c.cancelados > 0 ? `-${c.cancelados}` : "0"}
                          </span>
                        </Td>
                        <Td mono>
                          <span
                            style={{
                              fontWeight: 600,
                              color: c.netos >= 0 ? "var(--ok)" : "var(--err)",
                            }}
                          >
                            {c.netos >= 0 ? `+${c.netos}` : c.netos}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {drilldown && (
          <DrilldownModal
            title={drilldown.title}
            items={drilldown.items}
            loading={drilldown.loading}
            onClose={() => setDrilldown(null)}
          />
        )}
      </section>
    </Shell>
  );
}

function ChurnChart({ serie }: { serie: ChurnPoint[] }) {
  if (serie.length === 0) return null;
  const W = 720;
  const H = 180;
  const pad = { top: 20, right: 16, bottom: 28, left: 36 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  const maxAbs = Math.max(
    1,
    ...serie.flatMap((s) => [s.nuevos, s.cancelados, Math.abs(s.netos)]),
  );
  const barW = innerW / serie.length;
  const groupW = barW * 0.6;
  const colW = groupW / 2;
  const zeroY = pad.top + innerH;

  return (
    <div className="card">
      <div className="card-inner">
        <div
          style={{
            display: "flex",
            gap: 18,
            marginBottom: 12,
            fontSize: 12,
            color: "var(--ink-3)",
          }}
        >
          <LegendDot color="var(--ok)" label="Nuevos" />
          <LegendDot color="var(--err)" label="Cancelados" />
          <LegendDot color="var(--ink-2)" label="Netos (línea)" />
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label="Gráfico de barras de nuevos vs cancelados por mes y línea de netos"
        >
          {/* eje X */}
          <line
            x1={pad.left}
            y1={zeroY}
            x2={W - pad.right}
            y2={zeroY}
            stroke="var(--hairline-strong)"
          />
          {serie.map((s, i) => {
            const cx = pad.left + barW * (i + 0.5);
            const nuevosH = (s.nuevos / maxAbs) * innerH;
            const canH = (s.cancelados / maxAbs) * innerH;
            return (
              <g key={s.mes}>
                <rect
                  x={cx - colW}
                  y={zeroY - nuevosH}
                  width={colW * 0.9}
                  height={nuevosH}
                  fill="var(--ok)"
                  opacity={0.85}
                />
                <rect
                  x={cx + colW * 0.1}
                  y={zeroY - canH}
                  width={colW * 0.9}
                  height={canH}
                  fill="var(--err)"
                  opacity={0.85}
                />
                <text
                  x={cx}
                  y={H - 6}
                  textAnchor="middle"
                  fontSize="9"
                  fill="var(--ink-3)"
                  fontFamily="monospace"
                >
                  {s.mes.slice(5)}
                </text>
              </g>
            );
          })}
          {/* línea de netos */}
          <polyline
            fill="none"
            stroke="var(--ink-2)"
            strokeWidth="2"
            points={serie
              .map((s, i) => {
                const cx = pad.left + barW * (i + 0.5);
                const cy = zeroY - (s.netos / maxAbs) * innerH;
                return `${cx},${cy}`;
              })
              .join(" ")}
          />
          {serie.map((s, i) => {
            const cx = pad.left + barW * (i + 0.5);
            const cy = zeroY - (s.netos / maxAbs) * innerH;
            return (
              <circle key={s.mes} cx={cx} cy={cy} r="3" fill="var(--ink)" />
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          background: color,
          borderRadius: 2,
        }}
        aria-hidden
      />
      {label}
    </span>
  );
}

function DrilldownModal({
  title,
  items,
  loading,
  onClose,
}: {
  title: string;
  items: TenantBrief[];
  loading: boolean;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,26,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px",
        zIndex: 50,
        overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="card"
        style={{ maxWidth: 720, width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-inner">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 16,
            }}
          >
            <div>
              <div className="eyebrow">Detalle</div>
              <h2 style={{ marginTop: 4 }}>{title}</h2>
            </div>
            <button type="button" className="btn-outline" onClick={onClose}>
              Cerrar
            </button>
          </div>

          {loading ? (
            <div className="eyebrow">Cargando…</div>
          ) : items.length === 0 ? (
            <p style={{ color: "var(--ink-3)" }}>
              No hay tenants en este grupo.
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--hairline-strong)" }}>
                  <Th>Alcaldía</Th>
                  <Th>Ubicación</Th>
                  <Th>Estado</Th>
                  <Th>Creada</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr
                    key={t.id}
                    style={{ borderBottom: "1px solid var(--hairline)" }}
                  >
                    <Td>
                      <a
                        href={`/tenants/${t.id}`}
                        style={{ fontWeight: 600, color: "var(--ink)" }}
                      >
                        {t.nombre}
                      </a>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: "var(--ink-3)",
                        }}
                      >
                        {t.slug}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ fontSize: 13 }}>{t.municipio}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {t.departamento}
                      </div>
                    </Td>
                    <Td>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color:
                            t.status === "active"
                              ? "var(--ok)"
                              : t.status === "trial"
                                ? "var(--warn)"
                                : "var(--ink-3)",
                        }}
                      >
                        {t.status}
                      </span>
                    </Td>
                    <Td mono>
                      {new Date(t.created_at).toLocaleDateString("es-CO", {
                        day: "2-digit",
                        month: "short",
                        year: "2-digit",
                      })}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const sectionTitleStyle: React.CSSProperties = {
  marginTop: 32,
  marginBottom: 12,
  fontSize: 14,
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--ink-3)",
  fontWeight: 500,
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

function KPI({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "danger";
}) {
  return (
    <div
      className="card"
      style={{
        borderLeft: tone === "danger" ? "3px solid var(--err)" : undefined,
      }}
    >
      <div className="card-inner" style={{ padding: 16 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--ink-3)",
            marginBottom: 6,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "var(--ink)",
          }}
        >
          {value}
        </div>
        {hint && (
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 20px",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--ink-3)",
  fontWeight: 500,
};

const tdStyle: React.CSSProperties = { padding: "12px 20px", fontSize: 14 };

function Th({ children }: { children: React.ReactNode }) {
  return <th style={thStyle}>{children}</th>;
}

function Td({
  children,
  mono,
}: {
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <td
      style={{
        ...tdStyle,
        fontFamily: mono ? "var(--font-mono)" : "var(--font-app)",
      }}
    >
      {children}
    </td>
  );
}
